import axios from "axios";
import {ApiRoute} from "@api/ApiRoutes";
import {ApiException} from "@/exceptions/ApiException";
import {ApiClientException} from "@/exceptions/ApiClientException";
import {useAuthStore} from "@store/AuthStore";
import {useAppContextStore} from "@store/AppContextStore";

/**
 * Determine if a generic error is a known API error
 * @param data
 */
export const isCustomError = (data: any): boolean => {
    return data && typeof data === 'object' && 'requestId' in data;
}

/**
 * Create a custom Exception class
 * @param ex
 */
export const createApiErrorException = (ex: any): ApiException | ApiClientException | Error => {
    if (!axios.isAxiosError(ex)) {
        return ex as Error;
    }

    const errorData = ex.response?.data;

    if (!isCustomError(errorData)) {
        return new ApiClientException(ex.message, ex?.response?.status);
    }

    return new ApiException(
        errorData.message ?? "No message",
        errorData.messageCode ?? "No message code",
        errorData?.status ?? -1,
        errorData?.messageTags ?? "No message tags",
        errorData?.requestId ?? "No request id",
    );

}

export const generateApiEndpoint = (args: ApiRoute, forceAccessType?: "public" | "private"): string => {

    const {path, module, solution} = args;

    const projectLogicId = useAppContextStore.getState().projectId;
    const catalogInitiativeId = useAppContextStore.getState().catalogInitiativeId;
    const isSignedUp = useAuthStore.getState().isAuthenticated;

    const accessType = forceAccessType ?? (!isSignedUp ? "public" : "private");

    const pieces: string[] = [];

    /**
     * Add module
     */
    if (module) {
        pieces.push(module);
    }

    /**
     * Add solution
     */
    if (solution) {
        pieces.push(solution);
    }

    /**
     * Add uri path
     */
    pieces.push(path);

    let endpoint = pieces.join("/");

    /**
     * automatically add common params
     */
    if (endpoint.includes(":projectLogicId")) {
        if ( !projectLogicId ) {
            throw new Error("projectLogicId is required for endpoint " + endpoint);
        }
        endpoint = endpoint.replace(":projectLogicId", '' + projectLogicId)
    }

    if (endpoint.includes(":accessType")) {
        if ( !accessType ) {
            throw new Error("accessType is required for endpoint " + endpoint);
        }
        endpoint = endpoint.replace(":accessType", '' + accessType)
    }

    if (endpoint.includes(":catalogInitiativeId")) {
        if ( !catalogInitiativeId ) {
            throw new Error("catalogInitiativeId is required for endpoint " + endpoint);
        }
        endpoint = endpoint.replace(":catalogInitiativeId", '' + catalogInitiativeId)
    }

    if (!args.hasOwnProperty('params') || !args.params) return endpoint;

    const pathParams = Object.entries(args.params);

    return pathParams.reduce((previousValue: string, [param, value]) => {
        return previousValue.replace(`:${param}`, '' + value)
    }, endpoint);
}
